import { CircularProgress } from '@ixigo/iui/CircularProgress';
import Typography from '@ixigo/iui/Typography';
import { useTimer } from '@repo/shared/hooks';
import { calculateTimeFraction, formatTimeWithUnit } from './helper';

export interface CountDownTimerProps {
	durationMs: number;
	startTimeEpoch: number;
	onExpire: () => void;
}

export const CountDownTimer = (props: CountDownTimerProps) => {
	const { durationMs = 0, onExpire } = props;

	const secondsRemaining = useTimer({
		direction: 'down',
		durationMs, // Duration to count down from
		onExpire,
	});

	if (durationMs <= 0) {
		return null;
	}

	const { time, timeUnit } = formatTimeWithUnit(secondsRemaining);
	const circleProgressValue =
		parseInt(
			calculateTimeFraction(secondsRemaining, durationMs / 1000).toFixed(0),
			10,
		) || 0;
	const accessibleDescription = `Time remaining: ${time} ${timeUnit}`;
	const progressPercentage = Math.max(0, Math.min(100, circleProgressValue));

	if (progressPercentage === 0) {
		return null;
	}

	return (
		<div
			className="shrink-0 flex items-center justify-center"
			role="timer"
			aria-label={accessibleDescription}
			aria-live="polite"
			aria-atomic="true"
		>
			<CircularProgress
				spinDirection="anticlockwise"
				size={80}
				value={progressPercentage}
				className="text-brand"
				thickness={6}
				aria-label={`${progressPercentage}% time remaining`}
			>
				<div className="flex flex-col text-center items-center justify-center">
					<Typography
						variant="body-sm"
						fontWeight="medium"
						className="text-primary"
					>
						{time}
					</Typography>
					<Typography variant="body-xs" className="text-secondary">
						{timeUnit}
					</Typography>
				</div>
			</CircularProgress>
		</div>
	);
};
